import { Grid, Text, useColorModeValue } from "@chakra-ui/react";
import { ErrorMessage } from "@hookform/error-message";
import { AnimatePresence } from "framer-motion";
import React, { ReactNode } from "react";

import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import { FeatureTargeting } from "@reflag/shared/featureTargetingAPI";

import { RuleSimpleCompanySelector } from "./RuleSimpleCompanySelector";
import { RuleSimpleSegmentSelector } from "./RuleSimpleSegmentSelector";
import { RuleSimpleUserSelector } from "./RuleSimpleUserSelector";

const sharedLabelProps = {
  fontSize: "sm",
  lineHeight: 8,
};

type RuleSimpleFormProps = {
  baseName: string;
  toolbar?: ReactNode;
};

export function RuleSimpleForm({ baseName, toolbar }: RuleSimpleFormProps) {
  const errorColor = useColorModeValue("red.500", "red.300");

  return (
    <AnimatePresence initial={false}>
      <Grid
        as="fieldset"
        gap={2}
        templateColumns="80px auto minmax(0, min-content)"
      >
        <Text {...sharedLabelProps}>Segments</Text>
        <ManagedFormControl
          gridColumn={!toolbar ? "span 2" : undefined}
          name={`${baseName}.segmentIds`}
          render={({ field }) => <RuleSimpleSegmentSelector {...field} />}
          rules={{
            deps: [baseName],
          }}
        />
        {toolbar}

        <Text {...sharedLabelProps}>Companies</Text>
        <ManagedFormControl
          gridColumn="span 2"
          name={`${baseName}.companyIds`}
          render={({ field }) => <RuleSimpleCompanySelector {...field} />}
          rules={{
            deps: [baseName],
          }}
        />
        <Text {...sharedLabelProps}>Users</Text>
        <ManagedFormControl
          gridColumn="span 2"
          name={`${baseName}.userIds`}
          render={({ field }) => <RuleSimpleUserSelector {...field} />}
          rules={{
            deps: [baseName],
          }}
        />
        <ErrorMessage
          name={`${baseName}.root`}
          render={({ message }) => (
            <Text color={errorColor} fontSize="sm" gridColumn="span 2">
              {message}
            </Text>
          )}
        />
      </Grid>
    </AnimatePresence>
  );
}

type RuleSimpleDisplayProps = {
  targeting: FeatureTargeting;
};

export const RuleSimpleDisplay = ({ targeting }: RuleSimpleDisplayProps) => {
  const { segmentIds, companyIds, userIds } = targeting;

  return (
    <Grid gap={2} templateColumns="80px auto">
      {!!segmentIds.length && (
        <>
          <Text {...sharedLabelProps}>Segments</Text>
          <RuleSimpleSegmentSelector value={segmentIds} isReadOnly />
        </>
      )}

      {!!companyIds.length && (
        <>
          <Text {...sharedLabelProps}>Companies</Text>
          <RuleSimpleCompanySelector value={companyIds} isReadOnly />
        </>
      )}

      {!!userIds.length && (
        <>
          <Text {...sharedLabelProps}>Users</Text>
          <RuleSimpleUserSelector value={userIds} isReadOnly />
        </>
      )}
    </Grid>
  );
};
